import {
  Avatar,
  ButtonDS,
  DialogModalDS,
  Spacer,
  TableDS,
  TextCapitalized,
  triggerToastDS,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import type { TableHeaderProps } from "@qivia/ui/src/designSystem/components/table/TableHeader";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  formatCurrency,
  formatDateWithoutDay,
} from "../../../../libSlice/materialUiFormats";
import { HomeChargingSupportingDocument } from "../homeChargingSupportingDocumentsAPI";
import {
  createHomeChargingSupportingDocumentsAsync,
  homeChargingSupportingDocumentsSlice,
  selectCreateHomeChargingSupportingDocumentsStatus,
  selectHomeChargingSupportingDocuments,
  selectHomeChargingSupportingDocumentsPeriod,
} from "../homeChargingSupportingDocumentsSlice";
import "../../../../i18n/i18n";

type SupportingDocumentToCreateDisplayed = Pick<
  HomeChargingSupportingDocument,
  "firstName" | "reimbursementAmount"
>;

export const CreateSupportingDocumentsModal = (props: {
  isVisible: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isVisible, onClose } = props;
  const supportingDocuments = useAppSelector(
    selectHomeChargingSupportingDocuments,
  );
  const supportingDocumentsPeriod = useAppSelector(
    selectHomeChargingSupportingDocumentsPeriod,
  );
  const createStatus = useAppSelector(
    selectCreateHomeChargingSupportingDocumentsStatus,
  );
  const [rowsChecked, setRowsChecked] = useState<
    HomeChargingSupportingDocument[]
  >([]);

  const supportingDocumentsToCreate = useMemo(
    () =>
      supportingDocuments.filter(
        (supportingDocument) =>
          supportingDocument.chargesCount > 0 &&
          supportingDocument.supportingDocument === null,
      ),
    [supportingDocuments],
  );

  useEffect(() => {
    if (isVisible) {
      setRowsChecked(supportingDocumentsToCreate);
    }
  }, [isVisible, supportingDocumentsToCreate]);

  const total = useMemo(
    () =>
      rowsChecked.reduce(
        (sum, supportingDocument) =>
          sum + supportingDocument.reimbursementAmount,
        0,
      ),
    [rowsChecked],
  );

  const onSubmit = useCallback(() => {
    if (!supportingDocumentsPeriod || rowsChecked.length === 0) {
      return;
    }
    void dispatch(
      createHomeChargingSupportingDocumentsAsync({
        month: supportingDocumentsPeriod.from,
        drivers: rowsChecked.map((supportingDocument) => ({
          uuid: supportingDocument.driverUuid,
        })),
      }),
    );
  }, [dispatch, rowsChecked, supportingDocumentsPeriod]);

  useEffect(() => {
    if (createStatus === "success") {
      triggerToastDS(
        t("home_charging_supporting_documents.createModal.success", {
          count: rowsChecked.length,
          period: supportingDocumentsPeriod
            ? formatDateWithoutDay(supportingDocumentsPeriod.from)
            : "",
        }),
        "SUCCESS",
      );
      dispatch(
        homeChargingSupportingDocumentsSlice.actions.resetCreateHomeChargingSupportingDocumentsStatus(),
      );
      dispatch(
        homeChargingSupportingDocumentsSlice.actions.resetHomeChargingSupportingDocumentsStatus(),
      );
      setRowsChecked([]);
      onClose();
    }
    if (createStatus === "failed") {
      triggerToastDS(
        t("home_charging_supporting_documents.createModal.error"),
        "ERROR",
      );
      dispatch(
        homeChargingSupportingDocumentsSlice.actions.resetCreateHomeChargingSupportingDocumentsStatus(),
      );
    }
  }, [
    createStatus,
    t,
    dispatch,
    onClose,
    rowsChecked.length,
    supportingDocumentsPeriod,
  ]);

  const headers = {
    firstName: { text: "" },
    reimbursementAmount: { text: "" },
  };

  const render =
    (row: HomeChargingSupportingDocument) =>
    (key: keyof SupportingDocumentToCreateDisplayed) => {
      switch (key) {
        case "firstName":
          return (
            <StyledRow>
              <Avatar
                color={"mint"}
                size={"S"}
                firstName={row.firstName}
                lastName={row.lastName}
              />
              <Spacer x={0.75} />
              {row.firstName} {row.lastName}
            </StyledRow>
          );
        case "reimbursementAmount":
          return (
            <StyledAmount>
              {formatCurrency(row.reimbursementAmount)}
            </StyledAmount>
          );
      }
    };

  if (!isVisible) {
    return null;
  }

  return (
    <DialogModalDS
      onClose={onClose}
      visible={isVisible}
      width={38}
      title={t("home_charging_supporting_documents.createModal.title")}
      bottomButtons={{
        leftButton: (
          <ButtonDS
            text={t("home_charging_supporting_documents.createModal.cancel")}
            format={"fill"}
            buttonType={"secondary"}
            onClick={onClose}
          />
        ),
        rightButton: (
          <ButtonDS
            text={t("home_charging_supporting_documents.createModal.validate")}
            format={"fill"}
            buttonType={"primary"}
            onClick={onSubmit}
            disabled={rowsChecked.length === 0 || createStatus === "processing"}
          />
        ),
      }}
    >
      <TableDS<
        keyof SupportingDocumentToCreateDisplayed,
        HomeChargingSupportingDocument
      >
        data={supportingDocumentsToCreate}
        headers={
          headers as Record<
            keyof HomeChargingSupportingDocument,
            TableHeaderProps
          >
        }
        render={render}
        rowsChecked={rowsChecked}
        setRowsChecked={setRowsChecked}
        width={35}
        height={25}
        header-checkbox-data-cy="header-checkbox-data-cy"
        headerOverlayed={
          <StyledHeader>
            <TextCapitalized>
              {t("home_charging_supporting_documents.createModal.selected", {
                count: rowsChecked.length,
              })}
            </TextCapitalized>
            <StyledRow>
              <TextCapitalized>
                {t("home_charging_supporting_documents.createModal.total")}
              </TextCapitalized>
              <Spacer x={0.5} />
              <StyledAmount>{formatCurrency(total)}</StyledAmount>
            </StyledRow>
          </StyledHeader>
        }
      />
    </DialogModalDS>
  );
};

const StyledRow = styled.div`
  display: flex;
  align-items: center;
`;

const StyledAmount = styled.div`
  ${typographies["Body/L"]}
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 30rem;
  ${typographies["Body/M"]};
  color: ${colors["colors/text/black"]};
`;
